import React from "react";
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { useLocation } from "react-router-dom";

import { Session } from "utils";
import { signOut } from "services";
import { useNavigateWithClient } from "hooks";

import { MOBILE_NAV_ITEMS } from "../routes";

const ICONS = {
  dashboard: DashboardOutlinedIcon,
  performance: TrendingUpIcon,
  profile: PersonOutlineOutlinedIcon,
};

/**
 * Slide-in navigation drawer (Dashboard / Performance / Profile), matching the
 * Figma. Navigation uses the shared `useNavigateWithClient` hook so the
 * `/:client` prefix is preserved. Items whose screen isn't built yet still
 * navigate — the router shows <NotAvailable/> for them.
 */
export default function MobileNavDrawer({ open, onClose }) {
  const location = useLocation();
  const navigateWithClient = useNavigateWithClient();

  const { logoUrl } = Session?.getClientMetadata?.() || {};

  const handleNavigate = (path) => {
    navigateWithClient(path);
    onClose();
  };

  // Mirrors desktop logout (ProfileMenu/LogoutDialog): clear the session, then
  // land on the (now mobile) `/:client/login`. Skips desktop's BETA_USER →
  // `/signin` branch since that route isn't in the mobile router.
  const handleLogout = () => {
    signOut();
    navigateWithClient("/login");
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: "78%", maxWidth: 320 } }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 4,
          py: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt="logo"
              sx={{ height: 28, maxWidth: 140, objectFit: "contain" }}
            />
          ) : (
            <>
              <Avatar sx={{ bgcolor: "warning.clr-300", width: 32, height: 32 }}>
                <Typography variant="body02-bold" color="shades.clr-white-900">
                  YL
                </Typography>
              </Avatar>
              <Typography variant="h6-semiBold" color="neutral.clr-800">
                YourLogo
              </Typography>
            </>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="close menu">
          <CloseIcon />
        </IconButton>
      </Box>

      <List sx={{ px: 2 }}>
        {MOBILE_NAV_ITEMS.map((item) => {
          const Icon = ICONS[item.key];
          const selected = location.pathname.endsWith(item.path);

          return (
            <ListItemButton
              key={item.key}
              selected={selected}
              onClick={() => handleNavigate(item.path)}
              sx={{
                borderRadius: 2,
                mb: 1,
                gap: 3,
                py: 3,
                "&.Mui-selected": {
                  bgcolor: "primary.clr-50",
                  "&:hover": { bgcolor: "primary.clr-50" },
                },
              }}
            >
              <Icon
                sx={{ color: selected ? "primary.clr-300" : "neutral.clr-600" }}
              />
              <Typography
                variant={selected ? "body01-bold" : "body01-medium"}
                color={selected ? "primary.clr-300" : "neutral.clr-700"}
              >
                {item.label}
              </Typography>
            </ListItemButton>
          );
        })}
      </List>

        {/* Pushes logout to the very bottom of the sidebar. */}
        <Box sx={{ flexGrow: 1 }} />

        <Divider sx={{ borderColor: "neutral.clr-200" }} />
        <List sx={{ px: 2, py: 2 }}>
          <ListItemButton
            onClick={handleLogout}
            sx={{ borderRadius: 2, gap: 3, py: 3 }}
          >
            <LogoutOutlinedIcon sx={{ color: "danger.clr-700" }} />
            <Typography variant="body01-medium" color="danger.clr-700">
              Log out
            </Typography>
          </ListItemButton>
        </List>
      </Box>
    </Drawer>
  );
}
